/**
 * NotificationDropdown Component
 *
 * Popover dropdown showing recent notifications. Uses Radix Popover for
 * click-outside dismissal, Escape key, and portal rendering.
 */

import { ListSkeleton } from '@/shared/components/loading-skeleton';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, IconKey } from '@/shared/components';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notificationKeys, useDismissNotification, useMarkAllAsRead, useMarkAsRead, useNotifications, useUnreadCount } from '../hooks';
import { NotificationItem } from './notification-item';

interface NotificationDropdownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function NotificationDropdown({ open, onOpenChange, children }: NotificationDropdownProps) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useNotifications({ page_size: 5 });
  const { data: unreadData } = useUnreadCount();

  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    }
  }, [open, queryClient]);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const dismiss = useDismissNotification();

  const notifications = data?.items ?? [];
  const unreadCount = unreadData?.count ?? 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent side='right' align='start' sideOffset={8} className='w-[380px] p-0'>
        {/* Header */}
        <div className='flex items-center justify-between px-4 py-3 border-b'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-semibold'>Notifications</span>
            {unreadCount > 0 && (
              <Badge variant='secondary' className='text-[10px] px-1.5 py-0'>
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant='ghost' size='sm' className='text-xs h-7 px-2' onClick={() => markAllAsRead.mutate()}>
              Mark all read
            </Button>
          )}
        </div>

        {/* List */}
        <div className='max-h-[400px] overflow-y-auto'>
          {isLoading ? (
            <div className='p-4'>
              <ListSkeleton rows={3} />
            </div>
          ) : notifications.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-8 text-center px-4'>
              <Icon icon={IconKey.Bell} size={40} className='text-muted-foreground/40 mb-3' />
              <p className='text-sm font-medium text-text'>You're all caught up!</p>
              <p className='text-xs text-muted-foreground mt-1'>No new notifications</p>
            </div>
          ) : (
            <div className='divide-y'>
              {notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  compact
                  onRead={(id) => markAsRead.mutate(id)}
                  onDismiss={(id) => dismiss.mutate(id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='border-t px-4 py-2'>
          <Link
            to='/notifications'
            viewTransition
            onClick={() => onOpenChange(false)}
            className='text-xs text-accent hover:text-accent/80 hover:underline transition-colors'
          >
            View all notifications →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
